import { useState } from 'react'
import type { Vehicle, VehicleColour } from '../data/vehicles'

/* Picking a paint, without re-quoting the car.

   The swatches used to swap the card onto the sister car standing in that
   paint, so every press re-read the floor: a different plate number, a different
   asking price, a different monthly. A visitor pressing a swatch is not
   asking for another car's figures — they are saying which colour they want
   the car in front of them in, and watching the price move under their hand
   reads as a bait.

   So the swatch is a preference now and nothing else. The card goes on
   quoting the car the grid handed it — its price, its instalment, its
   deposit, its photographs — and the only thing the pick reaches is the
   booking: it is carried into the form, filed with the order, and the desk
   confirms the paint when it calls. Nothing here fetches anything. */

export type ColourPick = {
  /** The paints to draw. The car's own and every other one the same trim
      and model year is standing on the floor in. */
  colours: VehicleColour[]
  /** The paint the visitor wants — the car's own until they press another.
      Null on a car nobody has recorded a colour on. */
  selected: VehicleColour | null
  /** The id of the ringed swatch, for the radio group. */
  selectedId: string
  select: (id: string) => void
}

export function useColourPick(vehicle: Vehicle): ColourPick {
  /* Kept with the car it was made on. A grid re-renders in place when a
     filter changes, so this hook can be handed a different car with the last
     one's pick still in state — and that pick is not this car's. */
  const [picked, setPicked] = useState<{ vehicleId: string; colourId: string } | null>(null)

  const colours = vehicle.colours ?? []
  /* Its own paint leads: that is the colour this car is actually standing
     in, so it is the one a booking gets if nobody touches a swatch. */
  const own = colours.find((option) => option.id === vehicle.id) ?? colours[0] ?? null

  const wanted =
    picked && picked.vehicleId === vehicle.id
      ? (colours.find((option) => option.id === picked.colourId) ?? null)
      : null

  const selected = wanted ?? own

  return {
    colours,
    selected,
    selectedId: selected?.id ?? vehicle.id,
    select: (id: string) => setPicked({ vehicleId: vehicle.id, colourId: id }),
  }
}

export default useColourPick
